// @flow
import { FC, useState } from 'react';
import {
  ICompany,
  IProjectMemberView,
  IProjectStore,
  ProjectMemberConfig,
} from '../../../store/companies/companiesTypes';
import { IOption } from '../../../components/Select/Select';
import Modal from '../../../components/Modal';
import messages from './Messages';
import Select from '../../../components/Select';
import Button from '../../../components/Button';
import Icon from '../../../components/Icon';
import {
  addProjectMemberFn,
  removeProjectMemberFn,
  updateProjectMemberFn,
} from '../../../store/companies/companiesSlice';
import Input from '../../../components/Input';
import { notifyError } from '../../../components/Notify/Notify';
import cloneDeep from 'lodash/cloneDeep';
import notify from '../../../components/Notify';
import i18n from '../../../i18n';

type Props = {
  company: ICompany;
  addMember: (
    projectMember: IProjectMemberView,
    companyId: number
  ) => ReturnType<typeof addProjectMemberFn>;
  removeMember: (
    projectMember: IProjectMemberView,
    companyId: number
  ) => ReturnType<typeof removeProjectMemberFn>;
  updateMember: (
    projectMember: IProjectMemberView,
    companyId: number
  ) => ReturnType<typeof updateProjectMemberFn>;
};

const projectMemberConfigCheckboxes: ProjectMemberConfig[] = [
  'canCreate',
  'visibleAllTasks',
  'admin',
  'implicit',
];

export const BatchAddMemberToProjects: FC<Props> = (props: Props) => {
  const rolesOptions = Object.values(props.company.roles)
    .filter((it) => it.roleType !== 'InProject')
    .map((it) => {
      return { label: it.displayName, value: it.id } as IOption;
    });
  const rolesInProjectOptions = Object.values(props.company.roles)
    .filter((it) => it.roleType === 'InProject')
    .map((it) => {
      return { label: it.displayName, value: it.id } as IOption;
    });

  const projectOptions = Object.values(props.company.projects)
    .filter((it) => !it.archived)
    .map((it) => {
      return { label: it.name, value: it.id };
    });

  const projectGroupNames = new Set<string>();

  Object.values(props.company.projects).forEach((it) => {
    if (!it.archived) {
      it.tags?.forEach((tag) => {
        projectGroupNames.add(tag);
      });
    }
  });
  const projectGroupNamesList = [...projectGroupNames];
  const [selectedRoles, setSelectedRoles] = useState<IOption[]>([]);
  const [selectedRoleInProject, setSelectedRoleInProject] =
    useState<IOption | null>(null);
  const [selectedProjects, setSelectedProjects] = useState<IOption[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [roleCheckBoxValues, setRoleCheckBoxValues] = useState({} as any);
  const [creating, setCreating] = useState(false);
  const create = () => {
    setCreating(true);
    const promisses: Promise<any>[] = [];
    selectedProjects.forEach((p) => {
      selectedRoles?.forEach((selectedRole) => {
        const project = props.company.projects[p.value] as IProjectStore;
        let currentMembership: IProjectMemberView = cloneDeep(
          project.projectRoleMembers[selectedRole!.value]
        );
        let add = false;
        if (!currentMembership) {
          currentMembership = {} as IProjectMemberView;
          add = true;
        }
        currentMembership.projectRoleId =
          Number(selectedRoleInProject?.value) ?? undefined;
        currentMembership.implicit = roleCheckBoxValues.implicit;
        currentMembership.canCreate = roleCheckBoxValues.canCreate;
        currentMembership.admin = roleCheckBoxValues.admin;
        currentMembership.visibleAllTasks = roleCheckBoxValues.visibleAllTasks;
        currentMembership.roleId = Number(selectedRole!.value);
        currentMembership.projectId = Number(p.value);
        if (add) {
          return props
            .addMember(currentMembership, props.company.id)
            .catch((e) => {
              notifyError(e);
            });
        }
        return props
          .updateMember(currentMembership, props.company.id)
          .catch((e) => {
            notifyError(e);
          });
      });
    });
    Promise.allSettled(promisses).finally(() => {
      notify(
        i18n.t('oprationDone', {
          ns: 'companyManagement',
        }) as string
      );
      setCreating(false);
    });
  };

  return (
    <>
      <Button onClick={() => setShowModal(true)}>
        <Icon type={'plus'} />
        {
          i18n.t('batchAddMembersToProjects', {
            ns: 'companyManagement',
          }) as string
        }
      </Button>
      <Modal
        isActive={showModal}
        title={
          i18n.t('addBatchMembers', {
            ns: 'companyManagement',
          }) as string
        }
        onClose={() => setShowModal(false)}
      >
        <Select
          onChangeValues={(e) => {
            setSelectedRoles(e);
          }}
          options={rolesOptions}
          values={selectedRoles}
          multiChoice={true}
          label={
            i18n.t('role', {
              ns: 'companyManagement',
            }) as string
          }
        />
        <Select
          onChangeValues={(e) => {
            if (!e[0]?.value) return;
            Object.values(props.company.projects)
              .filter((p) => {
                return p.tags?.includes(e[0].value.toString()) && !p.archived;
              })
              .forEach((p) => {
                if (!selectedProjects.find((it) => it.value === p.id)) {
                  selectedProjects.push({ label: p.name, value: p.id });
                }
              });
            setSelectedProjects([...selectedProjects]);
          }}
          options={projectGroupNamesList.map((it) => ({
            label: it,
            value: it,
          }))}
          values={[]}
          label={
            i18n.t('projectGroups', {
              ns: 'companyManagement',
            }) as string
          }
        />

        <Select
          onChangeValues={(e) => {
            setSelectedProjects(e);
          }}
          options={projectOptions}
          values={selectedProjects}
          label={messages.projects(props.company.name)}
          multiChoice={true}
        />

        <br />
        <hr />
        <Select
          onChangeValues={(e) => {
            setSelectedRoleInProject(e[0] || null);
          }}
          options={rolesInProjectOptions}
          values={selectedRoleInProject ? [selectedRoleInProject] : []}
          label={
            i18n.t('roleInProjectEx', {
              ns: 'companyManagement',
            }) as string
          }
        />

        {projectMemberConfigCheckboxes.map((key) => {
          return (
            <span key={'key'}>
              <Input
                checked={Boolean(roleCheckBoxValues[key])}
                id={key}
                label={i18n.t(`${key}`, { ns: 'companyManagement' }) as string}
                name={key}
                onChange={(e) => {
                  const newValue = { ...roleCheckBoxValues };
                  newValue[e.target.name] = e.target.checked;
                  setRoleCheckBoxValues(newValue);
                }}
                type="checkbox"
              />
            </span>
          );
        })}

        <Button loading={creating} onClick={() => create()}>
          {
            i18n.t('addOrUpdateRole', {
              ns: 'companyManagement',
            }) as string
          }
        </Button>
      </Modal>
    </>
  );
};
